<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import AccountConversationApi from 'dashboard/api/conversations';
import MessageApi from 'dashboard/api/inbox/message';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const dialogRef = ref(null);
const selectedAgentId = ref('');
const isSubmitting = ref(false);

const { agentsList } = useAgentsList(false);

const currentChat = computed(() => store.getters.getSelectedChat || {});
const currentUser = computed(() => store.getters.getCurrentUser || {});
const currentAssigneeId = computed(() => currentChat.value?.meta?.assignee?.id);
const currentContactId = computed(() => currentChat.value?.meta?.sender?.id);
const inboxId = computed(() => currentChat.value?.inbox_id);

const assignableAgents = computed(() =>
  (agentsList.value || []).filter(agent => {
    if (!agent?.id) return false;

    return agent.id !== currentAssigneeId.value;
  })
);

const selectedAgent = computed(() =>
  assignableAgents.value.find(
    agent => String(agent.id) === selectedAgentId.value
  )
);

const isConfirmDisabled = computed(
  () => !selectedAgent.value || isSubmitting.value || !currentContactId.value
);

function openDialog() {
  selectedAgentId.value = '';
  dialogRef.value?.open();
}

function closeDialog() {
  dialogRef.value?.close();
}

async function handleConfirm() {
  if (isConfirmDisabled.value) return;

  isSubmitting.value = true;

  try {
    const { data: newConversation } = await AccountConversationApi.create({
      inbox_id: inboxId.value,
      contact_id: currentContactId.value,
      assignee_id: selectedAgent.value.id,
      status: 'open',
    });

    await MessageApi.create({
      conversationId: currentChat.value.id,
      message: t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.PUBLIC_NOTE', {
        agentName: selectedAgent.value.name,
      }),
      private: false,
    });

    await MessageApi.create({
      conversationId: newConversation.id,
      message: t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.PRIVATE_NOTE', {
        agentName:
          currentUser.value?.name ||
          currentUser.value?.available_name ||
          t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.AGENT_FALLBACK'),
      }),
      private: true,
    });

    await store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: 'resolved',
    });

    useAlert(
      t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.SUCCESS', {
        agentName: selectedAgent.value.name,
      })
    );

    closeDialog();
    router.push(
      `/app/accounts/${newConversation.account_id}/conversations/${newConversation.id}`
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
}

defineExpose({ openDialog, closeDialog });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="$t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.TITLE')"
    :description="$t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.DESCRIPTION')"
    :confirm-button-label="
      $t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.CONFIRM')
    "
    :cancel-button-label="$t('CONVERSATION.HEADER.CLOSE')"
    :disable-confirm-button="isConfirmDisabled"
    :is-loading="isSubmitting"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <label class="flex flex-col gap-2 text-sm font-medium text-n-slate-12">
        {{ $t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.AGENT_LABEL') }}
        <select
          v-model="selectedAgentId"
          class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
        >
          <option value="">
            {{
              $t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.AGENT_PLACEHOLDER')
            }}
          </option>
          <option
            v-for="agent in assignableAgents"
            :key="agent.id"
            :value="String(agent.id)"
          >
            {{ agent.name }}
          </option>
        </select>
      </label>

      <p v-if="!assignableAgents.length" class="mb-0 text-sm text-n-slate-11">
        {{ $t('CONVERSATION.NATIVE_ACTIONS.CLEAN_TRANSFER.NO_AGENTS') }}
      </p>
    </div>
  </Dialog>
</template>
